import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLang } from "../../context/LanguageContext";

/**
 * Full-screen in-app camera for document capture (mobile web).
 *
 * We intentionally do **not** load OpenCV.js / jscanify in the browser: on many
 * Android devices WASM compile hangs the tab after the JS download (exactly
 * the “100% then frozen” failure mode). The scan API embeds photos **as framed**
 * (optional max edge downscale only)—no server perspective flattening.
 *
 * While previewing, passport-style guide proportions help users align before capture.
 */

export interface DocumentCameraCaptureMeta {
  /** Legacy flag; client capture does not dewarp — kept for typing compatibility. */
  flattened: boolean;
  width: number;
  height: number;
}

export interface DocumentCameraViewProps {
  open: boolean;
  onClose: () => void;
  /** Receives one captured page; called once per shutter press. */
  onCapture: (file: File, meta: DocumentCameraCaptureMeta) => void;
  /**
   * Passport instructive flow: spread guide only, client copy, single primary action label.
   * ``first`` uses full help text; ``subsequent`` uses a shorter hint after the first capture.
   */
  passportGuidePhase?: "first" | "subsequent" | null;
  /** 1-based label for passport header (PAGE 1, …). Ignored when ``passportGuidePhase`` is null. */
  passportCapturePageNumber?: number;
  /** Opens the passport photo examples sheet (good vs bad shots); optional ``?`` control when provided. */
  onPassportOpenExamples?: () => void;
  /**
   * When ``passportGuidePhase`` is active but this is true, use neutral copy (page label, align hints,
   * capture toast) instead of passport-specific strings — same capture geometry as passport mode.
   */
  genericDocumentCamera?: boolean;
}

type LoadState = "idle" | "starting" | "ready" | "no-camera" | "permission-denied" | "error";

/** Alignment overlay shape: one passport page vs. open spread (~two ID-3 widths). */
type AlignmentGuideMode = "single" | "spread";

const CAPTURE_JPEG_QUALITY = 0.92;
const VIDEO_META_WAIT_MS = 12_000;
/** Downscale large crops so JPEG encode stays responsive on mid-range phones. */
const PASSPORT_CAPTURE_MAX_EDGE = 3200;
/** Expand guide-based crop by a margin so edge text is less likely to be cut. */
const PASSPORT_CAPTURE_MARGIN_RATIO = 0.1;

function genFilename(): string {
  const t = new Date();
  const pad = (x: number) => String(x).padStart(2, "0");
  return `scan-${t.getFullYear()}${pad(t.getMonth() + 1)}${pad(t.getDate())}-${pad(
    t.getHours(),
  )}${pad(t.getMinutes())}${pad(t.getSeconds())}-${Math.floor(Math.random() * 1000)
    .toString()
    .padStart(3, "0")}.jpg`;
}

/** Android Chrome often leaves videoWidth/videoHeight at 0 until loadedmetadata / resize. */
function waitForVideoPixels(video: HTMLVideoElement, signal: { aborted: boolean }): Promise<void> {
  const hasDims = () => video.videoWidth > 0 && video.videoHeight > 0;
  if (hasDims()) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      window.clearTimeout(timer);
      video.removeEventListener("loadedmetadata", onReady);
      video.removeEventListener("resize", onReady);
      video.removeEventListener("canplay", onReady);
    };
    const onReady = () => {
      if (hasDims()) {
        cleanup();
        resolve();
      }
    };
    const timer = window.setTimeout(() => {
      cleanup();
      if (signal.aborted) return;
      reject(new Error("video-metadata-timeout"));
    }, VIDEO_META_WAIT_MS);
    video.addEventListener("loadedmetadata", onReady);
    video.addEventListener("resize", onReady);
    video.addEventListener("canplay", onReady);
    onReady();
  });
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

/**
 * Maps a rectangle in the video element's layout box (CSS px, top-left relative to element)
 * to intrinsic video pixel coordinates when the video is rendered with ``object-fit: cover``
 * and default centered object-position.
 */
function guideLayoutRectToVideoIntrinsicRect(
  video: HTMLVideoElement,
  guideRelToVideoEl: { x: number; y: number; w: number; h: number },
): { sx: number; sy: number; sw: number; sh: number } {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  const W = video.clientWidth;
  const H = video.clientHeight;
  const { x: gx, y: gy, w: gw, h: gh } = guideRelToVideoEl;
  if (!vw || !vh || !W || !H || gw <= 2 || gh <= 2) {
    return { sx: 0, sy: 0, sw: vw, sh: vh };
  }
  const coverScale = Math.max(W / vw, H / vh);
  const visibleW = W / coverScale;
  const visibleH = H / coverScale;
  const sx0 = (vw - visibleW) / 2;
  const sy0 = (vh - visibleH) / 2;

  let sx = sx0 + (gx / W) * visibleW;
  let sy = sy0 + (gy / H) * visibleH;
  let sw = (gw / W) * visibleW;
  let sh = (gh / H) * visibleH;

  sx = Math.floor(clamp(sx, 0, vw - 1));
  sy = Math.floor(clamp(sy, 0, vh - 1));
  sw = Math.ceil(clamp(sw, 1, vw - sx));
  sh = Math.ceil(clamp(sh, 1, vh - sy));
  return { sx, sy, sw, sh };
}

/** Add symmetric margin around intrinsic crop, clamped to frame bounds. */
function expandIntrinsicCropRect(
  rect: { sx: number; sy: number; sw: number; sh: number },
  maxW: number,
  maxH: number,
  marginRatio: number,
): { sx: number; sy: number; sw: number; sh: number } {
  if (marginRatio <= 0) return rect;
  const padX = Math.round(rect.sw * marginRatio);
  const padY = Math.round(rect.sh * marginRatio);
  const sx = clamp(rect.sx - padX, 0, Math.max(0, maxW - 1));
  const sy = clamp(rect.sy - padY, 0, Math.max(0, maxH - 1));
  const ex = clamp(rect.sx + rect.sw + padX, sx + 1, maxW);
  const ey = clamp(rect.sy + rect.sh + padY, sy + 1, maxH);
  return {
    sx: Math.floor(sx),
    sy: Math.floor(sy),
    sw: Math.max(1, Math.ceil(ex - sx)),
    sh: Math.max(1, Math.ceil(ey - sy)),
  };
}

/** Fallback when videoWidth is still 0 (some WebViews). */
function getVideoPixelSize(video: HTMLVideoElement): { w: number; h: number } {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (vw > 0 && vh > 0) return { w: vw, h: vh };
  const stream = video.srcObject;
  if (!stream || !(stream instanceof MediaStream)) return { w: 0, h: 0 };
  const track = stream.getVideoTracks()[0];
  const s = track?.getSettings?.() ?? {};
  const w = typeof s.width === "number" ? s.width : 0;
  const h = typeof s.height === "number" ? s.height : 0;
  return { w, h };
}

export default function DocumentCameraView({
  open,
  onClose,
  onCapture,
  passportGuidePhase = null,
  passportCapturePageNumber = 1,
  onPassportOpenExamples,
  genericDocumentCamera = false,
}: DocumentCameraViewProps) {
  const { t } = useLang();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  /** Passport viewfinder: green frame (must match what the user aligns to). */
  const passportGuideFrameRef = useRef<HTMLDivElement | null>(null);
  const captureCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [loadState, setLoadState] = useState<LoadState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  /** Increment to re-run the camera acquisition effect (recover from idle / track ended). */
  const [cameraRetryNonce, setCameraRetryNonce] = useState(0);
  const [busyCapturing, setBusyCapturing] = useState(false);
  /** Brief confirmation after each successful shutter press (multi-page flow stays on camera). */
  const [captureToast, setCaptureToast] = useState<string | null>(null);
  const [shutterFlash, setShutterFlash] = useState(false);
  const [alignmentGuideMode, setAlignmentGuideMode] = useState<AlignmentGuideMode>("single");
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const feedbackTimersRef = useRef<{ toast: number | null; flash: number | null }>({
    toast: null,
    flash: null,
  });

  const clearFeedbackTimers = useCallback(() => {
    const { toast, flash } = feedbackTimersRef.current;
    if (toast !== null) window.clearTimeout(toast);
    if (flash !== null) window.clearTimeout(flash);
    feedbackTimersRef.current = { toast: null, flash: null };
  }, []);

  const cleanupCamera = useCallback(() => {
    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch {
          /* ignore */
        }
      });
      streamRef.current = null;
    }
    const video = videoRef.current;
    if (video) {
      try {
        video.srcObject = null;
      } catch {
        /* ignore */
      }
    }
  }, []);

  const bumpCameraRetry = useCallback(() => {
    setErrorMessage(null);
    setCameraRetryNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      const stream = streamRef.current;
      const video = videoRef.current;
      if (!stream || !video) return;
      const tr = stream.getVideoTracks()[0];
      if (tr?.readyState === "ended") {
        cleanupCamera();
        setLoadState("error");
        setErrorMessage(t("documentScanner.cameraStreamEnded"));
        return;
      }
      void video.play().catch(() => {});
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [open, cleanupCamera, t]);

  useEffect(() => {
    if (!open) {
      clearFeedbackTimers();
      setCaptureToast(null);
      setShutterFlash(false);
      cleanupCamera();
      setLoadState("idle");
      setErrorMessage(null);
      setAlignmentGuideMode("single");
      setTorchOn(false);
      setTorchSupported(false);
      setCameraRetryNonce(0);
      return;
    }
    if (passportGuidePhase) {
      setAlignmentGuideMode("spread");
    }
    let cancelled = false;
    let videoTrack: MediaStreamTrack | null = null;

    const onTrackEnded = () => {
      if (cancelled) return;
      cleanupCamera();
      setLoadState("error");
      setErrorMessage(t("documentScanner.cameraStreamEnded"));
    };

    clearFeedbackTimers();
    setCaptureToast(null);
    setShutterFlash(false);
    setLoadState("starting");
    setErrorMessage(null);
    const abortSig = { aborted: false };

    (async () => {
      try {
        if (
          typeof navigator === "undefined" ||
          !navigator.mediaDevices ||
          typeof navigator.mediaDevices.getUserMedia !== "function"
        ) {
          throw new Error("camera-unavailable");
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: "environment" },
          },
        });
        if (cancelled) {
          stream.getTracks().forEach((tr) => tr.stop());
          return;
        }
        streamRef.current = stream;
        videoTrack = stream.getVideoTracks()[0] ?? null;
        videoTrack?.addEventListener("ended", onTrackEnded);

        const video = videoRef.current;
        if (!video) {
          stream.getTracks().forEach((tr) => tr.stop());
          streamRef.current = null;
          throw new Error("video-element-missing");
        }
        video.srcObject = stream;
        video.setAttribute("playsinline", "true");
        video.muted = true;

        try {
          await video.play();
        } catch {
          await new Promise((r) => window.setTimeout(r, 250));
          try {
            await video.play();
          } catch {
            videoTrack?.removeEventListener("ended", onTrackEnded);
            videoTrack = null;
            cleanupCamera();
            setLoadState("error");
            setErrorMessage(t("documentScanner.cameraPlayBlocked"));
            return;
          }
        }

        if (cancelled) return;

        let { w, h } = getVideoPixelSize(video);
        if (!w || !h) {
          try {
            await waitForVideoPixels(video, abortSig);
          } catch {
            /* timeout — fall through and re-read track settings */
          }
        }
        ({ w, h } = getVideoPixelSize(video));
        if (!w || !h) {
          videoTrack?.removeEventListener("ended", onTrackEnded);
          videoTrack = null;
          cleanupCamera();
          setLoadState("error");
          setErrorMessage(t("documentScanner.cameraVideoNotReady"));
          return;
        }
        setLoadState("ready");
      } catch (err) {
        if (cancelled) return;
        cleanupCamera();
        videoTrack?.removeEventListener("ended", onTrackEnded);
        videoTrack = null;
        const name =
          (err instanceof Error && (err as DOMException).name) || (err instanceof Error ? err.message : "");
        const msg = err instanceof Error ? err.message : "";
        if (name === "NotAllowedError" || name === "SecurityError") {
          setLoadState("permission-denied");
        } else if (name === "NotFoundError" || name === "OverconstrainedError" || name === "camera-unavailable") {
          setLoadState("no-camera");
        } else {
          setLoadState("error");
          if (msg === "video-no-pixels" || msg === "video-metadata-timeout") {
            setErrorMessage(t("documentScanner.cameraVideoNotReady"));
          } else if (msg === "video-element-missing") {
            setErrorMessage(t("documentScanner.cameraInitError"));
          } else {
            setErrorMessage(msg || t("documentScanner.cameraInitError"));
          }
        }
      }
    })();

    return () => {
      cancelled = true;
      abortSig.aborted = true;
      videoTrack?.removeEventListener("ended", onTrackEnded);
      clearFeedbackTimers();
      setCaptureToast(null);
      setShutterFlash(false);
      cleanupCamera();
    };
  }, [open, passportGuidePhase, cameraRetryNonce, cleanupCamera, clearFeedbackTimers, t]);

  useEffect(() => {
    if (!open || loadState !== "ready") return;
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track?.getCapabilities) {
      setTorchSupported(false);
      return;
    }
    const caps = track.getCapabilities() as MediaTrackCapabilities & { torch?: boolean };
    setTorchSupported(Boolean(caps.torch));
  }, [open, loadState]);

  const toggleTorch = useCallback(async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track || !torchSupported) return;
    try {
      const next = !torchOn;
      await track.applyConstraints({
        advanced: [{ torch: next } as MediaTrackConstraintSet],
      });
      setTorchOn(next);
    } catch {
      setTorchOn(false);
    }
  }, [torchOn, torchSupported]);

  const captureFrame = useCallback(async () => {
    const video = videoRef.current;
    if (!video || busyCapturing) return;
    setBusyCapturing(true);
    setErrorMessage(null);
    try {
      let { w, h } = getVideoPixelSize(video);
      if (!w || !h) {
        await waitForVideoPixels(video, { aborted: false });
        ({ w, h } = getVideoPixelSize(video));
      }
      if (!w || !h) throw new Error("video-not-ready");

      const captureCanvas = captureCanvasRef.current ?? document.createElement("canvas");
      captureCanvasRef.current = captureCanvas;
      const ctx = captureCanvas.getContext("2d");
      if (!ctx) throw new Error("canvas-2d-unavailable");

      let drawSrc = { sx: 0, sy: 0, sw: w, sh: h };
      let outW = w;
      let outH = h;

      if (passportGuidePhase && passportGuideFrameRef.current) {
        const vRect = video.getBoundingClientRect();
        const gRect = passportGuideFrameRef.current.getBoundingClientRect();
        const guideRel = {
          x: gRect.left - vRect.left,
          y: gRect.top - vRect.top,
          w: gRect.width,
          h: gRect.height,
        };
        const intrinsic = guideLayoutRectToVideoIntrinsicRect(video, guideRel);
        drawSrc = expandIntrinsicCropRect(
          intrinsic,
          w,
          h,
          PASSPORT_CAPTURE_MARGIN_RATIO,
        );
        outW = drawSrc.sw;
        outH = drawSrc.sh;
        if (Math.max(outW, outH) > PASSPORT_CAPTURE_MAX_EDGE) {
          const r = PASSPORT_CAPTURE_MAX_EDGE / Math.max(outW, outH);
          outW = Math.round(outW * r);
          outH = Math.round(outH * r);
        } else {
          outW = Math.round(outW);
          outH = Math.round(outH);
        }
      }

      captureCanvas.width = outW;
      captureCanvas.height = outH;
      ctx.drawImage(video, drawSrc.sx, drawSrc.sy, drawSrc.sw, drawSrc.sh, 0, 0, outW, outH);

      const blob: Blob = await new Promise((resolve, reject) => {
        const tmr = window.setTimeout(() => reject(new Error("toBlob-timeout")), 15_000);
        captureCanvas.toBlob(
          (b) => {
            window.clearTimeout(tmr);
            if (b) resolve(b);
            else reject(new Error("toBlob returned null"));
          },
          "image/jpeg",
          CAPTURE_JPEG_QUALITY,
        );
      });
      const file = new File([blob], genFilename(), { type: "image/jpeg" });
      onCapture(file, {
        flattened: false,
        width: captureCanvas.width,
        height: captureCanvas.height,
      });

      clearFeedbackTimers();
      setCaptureToast(
        passportGuidePhase && !genericDocumentCamera
          ? t("documentScanner.passportCaptureToast")
          : t("documentScanner.cameraPageCaptured"),
      );
      feedbackTimersRef.current.toast = window.setTimeout(() => {
        setCaptureToast(null);
        feedbackTimersRef.current.toast = null;
      }, 2800);

      setShutterFlash(true);
      feedbackTimersRef.current.flash = window.setTimeout(() => {
        setShutterFlash(false);
        feedbackTimersRef.current.flash = null;
      }, 220);

      if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
        navigator.vibrate(35);
      }
    } catch (err) {
      setErrorMessage(
        err instanceof Error && err.message === "video-not-ready"
          ? t("documentScanner.cameraVideoNotReady")
          : (err instanceof Error && err.message) || t("documentScanner.captureError"),
      );
    } finally {
      setBusyCapturing(false);
    }
  }, [busyCapturing, clearFeedbackTimers, genericDocumentCamera, onCapture, passportGuidePhase, t]);

  const alignmentGuideStyle = useMemo(() => {
    if (alignmentGuideMode === "spread") {
      return {
        width: "min(96vw, min(420px, calc(48vh * 176 / 125)))",
        aspectRatio: "176 / 125",
      } as const;
    }
    return {
      width: "min(91vw, min(370px, calc(52vh * 88 / 125)))",
      aspectRatio: "88 / 125",
    } as const;
  }, [alignmentGuideMode]);

  const overlayHint = useMemo(() => {
    if (loadState === "starting") return t("documentScanner.cameraStarting");
    if (loadState === "permission-denied") return t("documentScanner.cameraPermissionDenied");
    if (loadState === "no-camera") return t("documentScanner.cameraNotFound");
    if (loadState === "error") return errorMessage || t("documentScanner.cameraInitError");
    if (loadState === "ready") {
      return errorMessage || t("documentScanner.cameraTapToCapture");
    }
    return "";
  }, [loadState, errorMessage, t]);

  if (!open) return null;

  const passportPageNum = Math.max(1, passportCapturePageNumber ?? 1);
  const isPassportCaptureUi = Boolean(passportGuidePhase);
  const passportGuideBoxStyle = {
    width: "min(94%, min(400px, calc((min(72vh, 100dvh - 280px)) * 176 / 125)))",
    aspectRatio: "176 / 125",
  } as const;

  const hintBg =
    loadState === "ready" && captureToast
      ? "rgba(16, 185, 129, 0.94)"
      : loadState === "ready" && errorMessage
        ? "rgba(220, 38, 38, 0.92)"
        : "rgba(0, 0, 0, 0.55)";

  return (
    <div className="fixed inset-0 z-[158] bg-black flex flex-col min-h-0" role="dialog" aria-modal="true">
      <style>{`
        @keyframes gm-doc-shutter-flash {
          0% { opacity: 0.72; }
          100% { opacity: 0; }
        }
        @keyframes gm-doc-frame-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.85; }
        }
        @media (prefers-reduced-motion: reduce) {
          .gm-doc-align-frame {
            animation: none !important;
          }
        }
      `}</style>

      {isPassportCaptureUi ? (
        <>
          <div className="shrink-0 z-[140] flex items-center justify-between px-4 pt-[max(12px,env(safe-area-inset-top))] pb-3 text-white bg-black">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
              className="min-w-[72px] h-11 text-[16px] font-medium text-white cursor-pointer touch-manipulation text-left"
            >
              {t("documentScanner.passportCancel")}
            </button>
            <h1 className="text-[15px] font-bold tracking-[0.08em] text-white uppercase flex-1 text-center px-2 truncate">
              {genericDocumentCamera
                ? t("documentScanner.pageLabel").replace("{{n}}", String(passportPageNum))
                : t("documentScanner.passportPageTitle").replace("{{n}}", String(passportPageNum))}
            </h1>
            {typeof onPassportOpenExamples === "function" ? (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onPassportOpenExamples();
                }}
                aria-label={t("documentScanner.passportExamplesAria")}
                className="shrink-0 w-11 h-11 rounded-full border border-white/35 bg-white/10 flex items-center justify-center active:bg-white/20 cursor-pointer touch-manipulation"
              >
                <i className="ri-question-line text-xl text-white" aria-hidden="true" />
              </button>
            ) : (
              <div className="shrink-0 w-[72px] min-w-[72px]" aria-hidden="true" />
            )}
          </div>

          <div className="flex-1 flex flex-col min-h-0 px-3 pt-1 pb-2">
            <div className="relative flex-1 min-h-[44vh] rounded-2xl overflow-hidden bg-neutral-950 ring-1 ring-white/12 shadow-inner">
              <video
                ref={videoRef}
                className="absolute inset-0 w-full h-full object-cover bg-black pointer-events-none select-none"
                playsInline
                muted
                autoPlay
              />

              {shutterFlash ? (
                <div
                  className="pointer-events-none absolute inset-0 z-[135] bg-white"
                  style={{
                    animation: "gm-doc-shutter-flash 220ms ease-out forwards",
                  }}
                  aria-hidden="true"
                />
              ) : null}

              {loadState === "ready" && captureToast ? (
                <div
                  role="status"
                  className="absolute left-1/2 top-1/2 z-[130] -translate-x-1/2 -translate-y-1/2 max-w-[88%] px-4 py-3 rounded-2xl text-[14px] font-medium text-white shadow-lg text-center"
                  style={{ backgroundColor: hintBg }}
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    <i className="ri-checkbox-circle-fill text-xl shrink-0 opacity-95" aria-hidden="true" />
                    <span>{captureToast}</span>
                  </span>
                </div>
              ) : null}

              {loadState === "ready" && !captureToast ? (
                <>
                  {passportCapturePageNumber <= 1 ? (
                    <div className="absolute top-4 left-1/2 z-[123] max-w-[calc(100%-1.5rem)] -translate-x-1/2 px-4 py-2.5 rounded-full bg-black/72 backdrop-blur-md border border-white/12 pointer-events-none shadow-lg">
                      <p className="text-[13px] text-white/95 text-center leading-snug">
                        {genericDocumentCamera
                          ? t("documentScanner.cameraAlignHint")
                          : t("documentScanner.passportViewfinderPill")}
                      </p>
                    </div>
                  ) : null}
                  <div className="absolute inset-0 z-[122] flex items-center justify-center px-4 py-12 pointer-events-none">
                    <div
                      className="relative max-h-full max-w-full shrink-0 box-border"
                      style={passportGuideBoxStyle}
                      aria-hidden="true"
                    >
                      <div
                        className="absolute inset-0 rounded-xl pointer-events-none"
                        style={{
                          boxShadow: "0 0 0 max(100vw, 100vh) rgba(0, 0, 0, 0.4)",
                        }}
                      />
                      <div
                        ref={passportGuideFrameRef}
                        className="gm-doc-align-frame relative h-full w-full rounded-xl box-border border-[2px] border-solid border-emerald-400/95 pointer-events-none"
                        style={{
                          animation: "gm-doc-frame-pulse 2.8s ease-in-out infinite",
                        }}
                      >
                        <div className="absolute left-0 top-0 h-9 w-9 border-l-[3px] border-t-[3px] border-emerald-200 rounded-tl-[10px]" />
                        <div className="absolute right-0 top-0 h-9 w-9 border-r-[3px] border-t-[3px] border-emerald-200 rounded-tr-[10px]" />
                        <div className="absolute bottom-0 right-0 h-9 w-9 border-b-[3px] border-r-[3px] border-emerald-200 rounded-br-[10px]" />
                        <div className="absolute bottom-0 left-0 h-9 w-9 border-b-[3px] border-l-[3px] border-emerald-200 rounded-bl-[10px]" />
                      </div>
                    </div>
                  </div>
                </>
              ) : null}

              {loadState !== "ready" ? (
                <div
                  className={`absolute inset-0 z-10 flex items-center justify-center px-6 bg-black/75 ${
                    loadState === "starting" ? "pointer-events-none" : "pointer-events-auto"
                  }`}
                >
                  <div className="w-full max-w-[300px] flex flex-col items-center gap-4 text-white text-center">
                    {loadState === "starting" ? (
                      <>
                        <i className="ri-loader-4-line text-4xl animate-spin opacity-90" aria-hidden="true" />
                        <p className="text-[14px] opacity-90">{t("documentScanner.cameraStarting")}</p>
                      </>
                    ) : loadState === "error" ||
                      loadState === "permission-denied" ||
                      loadState === "no-camera" ? (
                      <>
                        <i className="ri-error-warning-line text-4xl opacity-90" aria-hidden="true" />
                        <p className="text-[14px] leading-relaxed opacity-90">
                          {loadState === "permission-denied"
                            ? t("documentScanner.cameraPermissionDenied")
                            : loadState === "no-camera"
                              ? t("documentScanner.cameraNotFound")
                              : errorMessage || t("documentScanner.cameraInitError")}
                        </p>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            bumpCameraRetry();
                          }}
                          className="mt-1 min-h-[48px] w-full max-w-[240px] rounded-xl bg-white text-neutral-900 text-[15px] font-semibold shadow-lg active:scale-[0.98] touch-manipulation cursor-pointer"
                        >
                          {t("documentScanner.cameraRetryButton")}
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>

            <p className="text-center text-[13px] text-white/88 mt-3 mb-1 px-2 leading-snug shrink-0">
              {genericDocumentCamera
                ? t("documentScanner.cameraTapToCapture")
                : t("documentScanner.passportCaptureFooterHint")}
            </p>
          </div>

          <div className="relative shrink-0 bg-black flex justify-center items-center pb-[max(28px,calc(env(safe-area-inset-bottom,0px)+44px))] pt-2 min-h-[108px] touch-manipulation">
            {torchSupported && loadState === "ready" ? (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  void toggleTorch();
                }}
                aria-label={t("documentScanner.passportFlashAria")}
                aria-pressed={torchOn}
                className={`absolute right-5 bottom-[calc(env(safe-area-inset-bottom,0px)+36px)] z-[142] h-[52px] w-[52px] rounded-full flex items-center justify-center border shadow-lg touch-manipulation transition-colors ${
                  torchOn
                    ? "border-amber-400/80 bg-amber-500/40 text-amber-50"
                    : "border-white/25 bg-neutral-900/90 text-white"
                }`}
              >
                <i
                  className={torchOn ? "ri-flashlight-fill text-xl" : "ri-flashlight-line text-xl"}
                  aria-hidden="true"
                />
              </button>
            ) : null}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void captureFrame();
              }}
              disabled={loadState !== "ready" || busyCapturing}
              aria-label={
                genericDocumentCamera
                  ? t("documentScanner.cameraShutter")
                  : t("documentScanner.passportTakePhoto")
              }
              className="relative z-[141] h-[78px] w-[78px] rounded-full bg-white border-4 border-white/35 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center shadow-xl active:scale-95 transition-transform duration-150 touch-manipulation select-none"
            >
              <span
                className={`block h-[58px] w-[58px] rounded-full border-2 border-[#1f2937] transition-colors duration-150 ${
                  captureToast ? "scale-105 border-emerald-700 bg-emerald-400" : "bg-white"
                }`}
                aria-hidden="true"
              />
            </button>
          </div>

        </>
      ) : (
        <>
          <div className="shrink-0 z-[140] bg-gradient-to-b from-black via-black/90 to-black/70 text-white pt-[max(10px,env(safe-area-inset-top))] pb-2 pointer-events-none">
            <div className="flex items-center justify-between px-4 pointer-events-auto gap-2">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClose();
                }}
                aria-label={t("documentScanner.close")}
                className="min-w-[64px] h-10 px-3 inline-flex items-center justify-center gap-1.5 rounded-full bg-black/55 active:bg-black/70 text-[14px] font-medium cursor-pointer touch-manipulation select-none shrink-0"
              >
                <i className="ri-arrow-left-line text-lg pointer-events-none" aria-hidden="true" />
                <span className="pointer-events-none">{t("documentScanner.close")}</span>
              </button>
              <div className="text-[12px] uppercase tracking-wide opacity-80 truncate text-center flex-1 min-w-0">
                {t("documentScanner.cameraTitle")}
              </div>
              <div className="w-[64px] shrink-0" aria-hidden="true" />
            </div>
          </div>

          <div className="relative flex-1 min-h-0 bg-black">
            <video
              ref={videoRef}
              className="absolute inset-0 h-full w-full object-cover bg-black pointer-events-none select-none"
              playsInline
              muted
              autoPlay
            />

            {shutterFlash ? (
              <div
                className="pointer-events-none absolute inset-0 z-[135] bg-white"
                style={{
                  animation: "gm-doc-shutter-flash 220ms ease-out forwards",
                }}
                aria-hidden="true"
              />
            ) : null}

            {loadState === "ready" && !captureToast ? (
              <div className="absolute inset-0 z-[122] flex items-center justify-center px-3 py-2 pointer-events-none min-h-0">
                <div
                  className="relative shrink-0 pointer-events-none box-border rounded-[10px] max-h-full max-w-full"
                  aria-hidden="true"
                  style={{
                    width: alignmentGuideStyle.width,
                    aspectRatio: alignmentGuideStyle.aspectRatio,
                  }}
                >
                  <div
                    className="pointer-events-none absolute inset-0 rounded-[10px]"
                    style={{
                      boxShadow: "0 0 0 max(100vw, 100vh) rgba(0, 0, 0, 0.42)",
                    }}
                  />
                  <div
                    className="gm-doc-align-frame pointer-events-none absolute inset-0 rounded-[10px] box-border border-[2px] border-solid border-emerald-200/95"
                    style={{
                      animation: "gm-doc-frame-pulse 2.8s ease-in-out infinite",
                    }}
                  />
                </div>
              </div>
            ) : null}

            <div
              role="status"
              aria-live="polite"
              aria-atomic="true"
              className="pointer-events-none absolute bottom-4 left-1/2 z-[125] max-w-[90vw] -translate-x-1/2 rounded-full px-3 py-2 text-center text-[13px] font-medium text-white shadow-lg"
              style={{ backgroundColor: hintBg }}
            >
              {loadState === "ready" && captureToast ? (
                <span className="inline-flex items-center justify-center gap-2 text-left">
                  <i className="ri-checkbox-circle-fill shrink-0 text-[18px] opacity-95" aria-hidden="true" />
                  <span>{captureToast}</span>
                </span>
              ) : (
                overlayHint
              )}
            </div>

            {loadState !== "ready" ? (
              <div
                className={`absolute inset-0 z-10 flex items-center justify-center bg-black/70 px-6 ${
                  loadState === "starting" ? "pointer-events-none" : "pointer-events-auto"
                }`}
              >
                <div className="flex w-full max-w-[320px] flex-col items-center gap-4 text-center text-white">
                  {loadState === "starting" ? (
                    <>
                      <i className="ri-loader-4-line text-4xl animate-spin opacity-90" aria-hidden="true" />
                      <p className="text-[14px] opacity-90">{t("documentScanner.cameraStarting")}</p>
                    </>
                  ) : loadState === "error" ||
                    loadState === "permission-denied" ||
                    loadState === "no-camera" ? (
                    <>
                      <i className="ri-error-warning-line text-4xl opacity-90" aria-hidden="true" />
                      <p className="text-[14px] leading-relaxed opacity-90">
                        {loadState === "permission-denied"
                          ? t("documentScanner.cameraPermissionDenied")
                          : loadState === "no-camera"
                            ? t("documentScanner.cameraNotFound")
                            : errorMessage || t("documentScanner.cameraInitError")}
                      </p>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          bumpCameraRetry();
                        }}
                        className="mt-1 min-h-[48px] w-full max-w-[240px] rounded-xl bg-white text-neutral-900 text-[15px] font-semibold shadow-lg active:scale-[0.98] touch-manipulation cursor-pointer"
                      >
                        {t("documentScanner.cameraRetryButton")}
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>

          <div className="relative z-[140] shrink-0 touch-manipulation bg-black px-4 pb-[max(28px,calc(env(safe-area-inset-bottom,0px)+52px))] pt-2 flex flex-col items-center gap-3">
            {loadState === "ready" && !captureToast ? (
              <>
                <p className="max-w-[min(96vw,380px)] px-1 text-center text-[13px] font-semibold leading-snug text-white/95">
                  {t("documentScanner.cameraAlignHint")}
                </p>
                <p className="max-w-[min(96vw,400px)] px-1 text-center text-[12px] leading-relaxed text-white/78">
                  {t(
                    alignmentGuideMode === "spread"
                      ? "documentScanner.cameraAlignSubtitleSpread"
                      : "documentScanner.cameraAlignSubtitle",
                  )}
                </p>
                <div
                  role="radiogroup"
                  aria-label={t("documentScanner.cameraFrameModeAria")}
                  className="inline-flex w-full max-w-[min(96vw,420px)] justify-center rounded-full bg-white/12 p-1 shadow-md ring-1 ring-white/30 backdrop-blur-sm"
                >
                  <button
                    type="button"
                    role="radio"
                    aria-checked={alignmentGuideMode === "single"}
                    onClick={() => setAlignmentGuideMode("single")}
                    className={`min-h-[40px] flex-1 rounded-full px-2.5 py-2 text-[12px] font-semibold tracking-wide transition-colors touch-manipulation sm:text-[13px] ${
                      alignmentGuideMode === "single"
                        ? "bg-emerald-600 text-white shadow-md"
                        : "text-white/95 hover:bg-white/14 active:bg-white/22"
                    }`}
                  >
                    {t("documentScanner.cameraFrameModeSingle")}
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={alignmentGuideMode === "spread"}
                    onClick={() => setAlignmentGuideMode("spread")}
                    className={`min-h-[40px] flex-1 rounded-full px-2.5 py-2 text-[12px] font-semibold tracking-wide transition-colors touch-manipulation sm:text-[13px] ${
                      alignmentGuideMode === "spread"
                        ? "bg-emerald-600 text-white shadow-md"
                        : "text-white/95 hover:bg-white/14 active:bg-white/22"
                    }`}
                  >
                    {t("documentScanner.cameraFrameModeSpread")}
                  </button>
                </div>
              </>
            ) : (
              <div className="min-h-[48px] shrink-0" aria-hidden="true" />
            )}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void captureFrame();
              }}
              disabled={loadState !== "ready" || busyCapturing}
              aria-label={t("documentScanner.cameraShutter")}
              className="relative z-[141] flex h-[78px] w-[78px] cursor-pointer touch-manipulation select-none items-center justify-center rounded-full border-4 border-white/40 bg-white shadow-lg transition-transform duration-150 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <span
                className={`block h-[58px] w-[58px] rounded-full border-2 border-[#1f2937] transition-colors duration-150 ${
                  captureToast ? "scale-105 border-emerald-700 bg-emerald-400" : "bg-white"
                }`}
                aria-hidden="true"
              />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
